import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchControllerEvidenceCoverage,
  fetchControllerExceptionRate,
  fetchControllerFailingControls,
  fetchControllerIPEStatus,
  fetchControllerRollforward,
  fetchControllerSampleSize,
  fetchControllerSoDVerified,
  fetchControllerTestSummary,
  fetchControllerToDToE,
  type ControllerEvidenceCoverageRow,
  type ControllerExceptionRateRow,
  type ControllerFailingControlRow,
  type ControllerIPEStatusRow,
  type ControllerRollforwardRow,
  type ControllerSampleSizeRow,
  type ControllerSoDVerifiedRow,
  type ControllerTestSummaryRow,
  type ControllerToDToERow,
} from '../../request/analytics/controllerReports.ts';
import { analyticsControllerQueryKeys } from '../../queries/analytics/keys.ts';

/**
 * React Query v5 hooks for the Controller-persona analytics dashboard
 * (Feature 5.3, reports #20-#28). Each report is its own independent query
 * so a single failing/slow widget doesn't block the rest of the dashboard.
 */

export function useControllerTestSummary(): UseQueryResult<ControllerTestSummaryRow[], Error> {
  return useQuery({
    queryKey: analyticsControllerQueryKeys.testSummary(),
    queryFn: fetchControllerTestSummary,
  });
}

export function useControllerFailingControls(): UseQueryResult<ControllerFailingControlRow[], Error> {
  return useQuery({
    queryKey: analyticsControllerQueryKeys.failingControls(),
    queryFn: fetchControllerFailingControls,
  });
}

export function useControllerExceptionRate(): UseQueryResult<ControllerExceptionRateRow[], Error> {
  return useQuery({
    queryKey: analyticsControllerQueryKeys.exceptionRate(),
    queryFn: fetchControllerExceptionRate,
  });
}

export function useControllerSampleSize(): UseQueryResult<ControllerSampleSizeRow[], Error> {
  return useQuery({
    queryKey: analyticsControllerQueryKeys.sampleSize(),
    queryFn: fetchControllerSampleSize,
  });
}

export function useControllerRollforward(): UseQueryResult<ControllerRollforwardRow[], Error> {
  return useQuery({
    queryKey: analyticsControllerQueryKeys.rollforward(),
    queryFn: fetchControllerRollforward,
  });
}

export function useControllerIPEStatus(): UseQueryResult<ControllerIPEStatusRow[], Error> {
  return useQuery({
    queryKey: analyticsControllerQueryKeys.ipeStatus(),
    queryFn: fetchControllerIPEStatus,
  });
}

export function useControllerToDToE(): UseQueryResult<ControllerToDToERow[], Error> {
  return useQuery({
    queryKey: analyticsControllerQueryKeys.todToe(),
    queryFn: fetchControllerToDToE,
  });
}

export function useControllerSoDVerified(): UseQueryResult<ControllerSoDVerifiedRow[], Error> {
  return useQuery({
    queryKey: analyticsControllerQueryKeys.sodVerified(),
    queryFn: fetchControllerSoDVerified,
  });
}

export function useControllerEvidenceCoverage(): UseQueryResult<ControllerEvidenceCoverageRow[], Error> {
  return useQuery({
    queryKey: analyticsControllerQueryKeys.evidenceCoverage(),
    queryFn: fetchControllerEvidenceCoverage,
  });
}
